import { useActionHandler, ActionHandlerOptions } from "./useActionHandler";
import {
  finishRegistration,
  profileUpdateUserInfo,
  updatePassword,
  linkGoogleAccount,
  unlinkGoogleAccount,
  deleteAccount,
  reservationsGetUserInfo,
  contactGetUserInfo,
  requestPasswordReset,
  resetPassword,
  verifyEmail,
  resendVerificationCode,
  submitPreRegistration,
  setUserRole,
  getUserProfile,
} from "@/server-side/actions/user";
import {
  FinishRegistrationUserInfoRequest,
  ProfileUpdateUserInfoRequest,
  UpdatePasswordRequest,
  PreRegistrationData,
} from "@/server-side/types/user";

export function useUserActions() {
  const { handleAction } = useActionHandler();

  const finishRegistrationAction = async (
    data: FinishRegistrationUserInfoRequest,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(finishRegistration(data), options);
  };

  const profileUpdateUserInfoAction = async (
    data: ProfileUpdateUserInfoRequest,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(profileUpdateUserInfo(data), options);
  };

  const updatePasswordAction = async (
    data: UpdatePasswordRequest,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(updatePassword(data), options);
  };

  const linkGoogleAccountAction = async (
    authCode: string,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(linkGoogleAccount(authCode), options);
  };

  const unlinkGoogleAccountAction = async (options?: ActionHandlerOptions) => {
    return handleAction(unlinkGoogleAccount(), options);
  };

  const deleteAccountAction = async (options?: ActionHandlerOptions) => {
    return handleAction(deleteAccount(), options);
  };

  const reservationsGetUserInfoAction = async (
    facilityId: string,
    userId: string,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(reservationsGetUserInfo(facilityId, userId), options);
  };

  const contactGetUserInfoAction = async (options?: ActionHandlerOptions) => {
    return handleAction(contactGetUserInfo(), options);
  };

  const requestPasswordResetAction = async (
    email: string,
    language: string,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(requestPasswordReset(email, language), options);
  };

  const resetPasswordAction = async (
    password: string,
    token: string,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(resetPassword(password, token), options);
  };

  const verifyEmailAction = async (
    userId: string,
    token: string,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(verifyEmail(userId, token), options);
  };

  const resendVerificationCodeAction = async (
    encryptedId: string,
    language: string,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(resendVerificationCode(encryptedId, language), options);
  };

  const submitPreRegistrationAction = async (
    data: PreRegistrationData,
    options?: ActionHandlerOptions
  ) => {
    return handleAction(submitPreRegistration(data), options);
  };

  const setUserRoleAction = async (role: string, options?: ActionHandlerOptions) => {
    return handleAction(setUserRole(role), options);
  };

  const getUserProfileAction = async (options?: ActionHandlerOptions) => {
    return handleAction(getUserProfile(), options);
  };

  return {
    finishRegistration: finishRegistrationAction,
    profileUpdateUserInfo: profileUpdateUserInfoAction,
    updatePassword: updatePasswordAction,
    linkGoogleAccount: linkGoogleAccountAction,
    unlinkGoogleAccount: unlinkGoogleAccountAction,
    deleteAccount: deleteAccountAction,
    reservationsGetUserInfo: reservationsGetUserInfoAction,
    contactGetUserInfo: contactGetUserInfoAction,
    requestPasswordReset: requestPasswordResetAction,
    resetPassword: resetPasswordAction,
    verifyEmail: verifyEmailAction,
    resendVerificationCode: resendVerificationCodeAction,
    submitPreRegistration: submitPreRegistrationAction,
    setUserRole: setUserRoleAction,
    getUserProfile: getUserProfileAction,
  };
}
